import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'

/** Common keys upstream might use for the gateway list, besides `data`. */
const LIST_KEYS = ['data', 'gateways', 'payment_gateways', 'items', 'list', 'results', 'result', 'records']

/**
 * Unwrap a gateway list out of whatever envelope the upstream API used —
 * `{ status, data }`, a Laravel paginator, or a differently-named key.
 * Upstream shape here is loosely documented, so this walks a few likely
 * keys before falling back to the first array found.
 */
function toList(res, depth = 0) {
  if (Array.isArray(res)) return res
  if (!res || typeof res !== 'object' || depth > 3) return []

  for (const key of LIST_KEYS) {
    if (key in res) {
      const found = toList(res[key], depth + 1)
      if (found.length) return found
    }
  }
  // Fallback: first array-valued property anywhere on this object.
  for (const value of Object.values(res)) {
    if (Array.isArray(value)) return value
  }
  return []
}

const displayName = (g) => g.display_name || g.name || g.title || g.gateway_name || g.slug || 'Payment gateway'

/** `is_active` comes back as 1/0, true/false, or "1"/"true" depending on the upstream call — normalize all of them. */
const isActive = (g) => {
  const v = g.is_active ?? g.status ?? g.active
  if (v == null) return true
  return typeof v === 'string' ? ['1', 'true', 'yes', 'active'].includes(v.toLowerCase()) : Boolean(Number(v)) || v === true
}

/** Cash on delivery — always offered, appended after the API gateways.
 * String id can't collide with the numeric upstream ids. */
const COD_OPTION = {
  id: 'cod',
  name: 'Cash on Delivery',
  slug: 'cod',
  description: 'Pay in cash when your order arrives at your doorstep.',
}

/**
 * Accordion payment options for the cart page. Expanding it lists the
 * available payment gateways (GET /payment-gateways) as selectable rows,
 * plus a fixed cash-on-delivery option at the end.
 * Same card/accordion treatment as <AddressBook/>.
 */
export default function PaymentOptions({ onSelect }) {
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const { data, isPending } = useQuery({
    queryKey: ['payment-gateways'],
    queryFn: () => api.payments.gateways(),
    retry: false,
  })
  const gateways = [...toList(data).filter(isActive), COD_OPTION]

  // First gateway counts as selected until the user picks one.
  const effectiveSelectedId = selectedId ?? gateways[0]?.id ?? null

  // Surface the effective selection to the parent — checkout needs the
  // gateway even when the user never clicks a row (default selection).
  const selectedGateway = gateways.find((g) => g.id === effectiveSelectedId) ?? null
  useEffect(() => {
    onSelect?.(selectedGateway)
  }, [selectedGateway?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (g) => setSelectedId(g.id)

  return (
    <div className="bg-white rounded-3xl border border-border/40 overflow-hidden shadow-sm">
      {/* Accordion header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full p-6 flex items-center gap-4 bg-muted/10 hover:bg-muted/20 transition-colors text-left"
      >
        <svg className="w-6 h-6 text-secondary shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="20" height="14" x="2" y="5" rx="2" />
          <line x1="2" x2="22" y1="10" y2="10" />
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-heading text-primary font-normal uppercase">PAYMENT OPTIONS</h3>
          <p className="text-[10px] font-mono font-normal text-primary/30 uppercase tracking-widest">
            {gateways.length > 0 ? `${gateways.length} GATEWAY${gateways.length > 1 ? 'S' : ''} AVAILABLE` : 'SECURE TRANSACTION POINT'}
          </p>
        </div>
        <svg
          className={`w-5 h-5 text-primary/40 shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {/* Accordion body */}
      {open && (
        <div className="border-t border-border p-6 md:p-8 space-y-4">
          {isPending ? (
            <div className="space-y-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border p-6 animate-pulse space-y-3">
                  <div className="h-4 w-48 rounded bg-gray-200" />
                  <div className="h-3 w-72 rounded bg-gray-100" />
                </div>
              ))}
            </div>
          ) : (
            gateways.map((g) => {
              const selected = effectiveSelectedId === g.id
              return (
                <div
                  key={g.id}
                  className={`rounded-2xl border py-3 px-4 transition-colors cursor-pointer ${selected ? 'border-secondary bg-secondary/3' : 'border-border bg-white'}`}
                  onClick={() => choose(g)}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      aria-label={`Pay with ${displayName(g)}`}
                      aria-pressed={selected}
                      className={`h-4 w-4 rounded-full border-2 grid place-items-center shrink-0 ${selected ? 'border-secondary' : 'border-border'}`}
                    >
                      {selected && <span className="h-1.5 w-1.5 rounded-full bg-secondary" />}
                    </button>
                    <span className="text-sm font-semibold text-primary uppercase tracking-wide">{displayName(g)}</span>
                    {selected && (
                      <span className="ml-auto px-2 py-0.5 rounded bg-secondary text-white text-[8px] font-bold tracking-widest uppercase">SELECTED</span>
                    )}
                  </div>
                  {g.description && (
                    <p className="mt-2 pl-7 text-xs text-primary/70 font-body normal-case leading-relaxed">{g.description}</p>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
